import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import { auth } from 'providers/firebaseAuth'

export const IMPERSONATION_KEY = 'hh.impersonation'

function readStorage() {
  try {
    const raw = localStorage.getItem(IMPERSONATION_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const ImpersonationContext = createContext({
  session: null,
  isImpersonating: false,
  canEdit: true,
  setSession: () => {},
  clearSession: () => {},
})

export const ImpersonationProvider = ({ children }) => {
  const [session, setSessionState] = useState(() => readStorage())

  // Pick up changes written by other tabs (e.g. a second TM workspace tab).
  useEffect(() => {
    const onStorage = e => {
      if (e.key === IMPERSONATION_KEY || e.key === null) setSessionState(readStorage())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // Clear session when Firebase signs out — covers both explicit logout and token
  // revocation. Doing this here (rather than in AuthContext) ensures the React
  // state is updated in the same tab; localStorage `storage` events only fire
  // in *other* tabs so an AuthContext-only removal would leave the banner visible.
  useEffect(() => {
    return onAuthStateChanged(auth, user => {
      if (!user) {
        try { localStorage.removeItem(IMPERSONATION_KEY) } catch { /* ignore */ }
        setSessionState(null)
      } else {
        // Detect stale impersonation session: Firebase still has the impersonation
        // user cached but localStorage was cleared (e.g. tab closed without End Session).
        // Sign out automatically so the real user can log in normally.
        user.getIdTokenResult().then(result => {
          // After signInWithCustomToken the custom claims surface at the top level; check there
          // first, then fall back to the nested "claims" object.
          const isImpersonation =
            result.claims?.isImpersonation === true || result.claims?.claims?.isImpersonation === true
          if (isImpersonation && !localStorage.getItem(IMPERSONATION_KEY)) {
            // During a fresh handoff the key is written a moment after sign-in. Re-check after a
            // short delay so only genuinely stale sessions (key never appears) are signed out.
            setTimeout(() => {
              if (!localStorage.getItem(IMPERSONATION_KEY)) signOut(auth).catch(() => {})
            }, 3000)
          }
        }).catch(() => {})
      }
    })
  }, [])

  const setSession = useCallback(data => {
    try {
      localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(data))
    } catch {
      /* storage unavailable */
    }
    setSessionState(data)
  }, [])

  const clearSession = useCallback(() => {
    try {
      localStorage.removeItem(IMPERSONATION_KEY)
    } catch {
      /* storage unavailable */
    }
    setSessionState(null)
  }, [])

  return (
    <ImpersonationContext.Provider
      value={{
        session,
        isImpersonating: !!session,
        canEdit: !session || session.mode !== 'view-only',
        setSession,
        clearSession,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  )
}

ImpersonationProvider.propTypes = {
  children: PropTypes.node,
}

/**
 * Returns the active impersonation session and derived helpers.
 *
 * `isImpersonating` — true when a session is active.
 * `canEdit`        — false during view-only sessions; true otherwise.
 *                    Gate edit actions on this — no need to read localStorage directly.
 * `setSession`     — called by ImpersonatePage after sign-in; writes to localStorage
 *                    and updates all consumers automatically.
 * `clearSession`   — called by ImpersonationBanner on End Session; also called
 *                    automatically when Firebase signs out.
 */
export const useImpersonationSession = () => useContext(ImpersonationContext)
